import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Award,
  MapPin,
  Sparkles,
  Cable,
  Clock,
  ArrowRight,
} from "lucide-react";
import { SITE, IMAGES } from "@/lib/site";

const VALUES = [
  {
    icon: ShieldCheck,
    title: "Erkend & Gecertificeerd",
    text: "Werken volgens de laatste NEN 1010 normen. Iedere installatie eindigt met een veiligheidscontrole.",
  },
  {
    icon: Award,
    title: "Ruime ervaring",
    text: "Vakkundig in complexe verbouwingen, storingzoeken en 3-fase upgrades. Geen klus te groot of te klein.",
  },
  {
    icon: MapPin,
    title: "Lokaal actief",
    text: "Vlaardingen, Schiedam, Rotterdam en alles binnen 25 km. Snel ter plaatse — vaak nog dezelfde week.",
  },
  {
    icon: Sparkles,
    title: "Maatwerk oplossingen",
    text: "Elke woning is uniek. We denken mee met jouw wensen, jouw stijl en jouw budget.",
  },
  {
    icon: Cable,
    title: "Strak kabelmanagement",
    text: "Netjes weggewerkte kabels, nette afwerkplaatjes en respect voor de rest van je woning.",
  },
  {
    icon: Clock,
    title: "Snelle service",
    text: "Duidelijke communicatie, korte lijnen. Meestal binnen 1 werkdag reactie op je aanvraag.",
  },
];

export default function OverOns() {
  return (
    <div data-testid="over-ons-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0A1128] text-white">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(${IMAGES.heroBg})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1128] via-[#0A1128]/90 to-[#0A1128]/50" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#FF6B00] mb-3">Over ons</p>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter max-w-4xl leading-[1.05]">
            Vakmanschap dat je <span className="text-[#FF6B00]">voelt</span>.
          </h1>
          <p className="mt-5 text-lg md:text-xl text-slate-300 max-w-2xl leading-relaxed">
            Care for Elektra is de lokale specialist voor huiseigenaren die kwaliteit belangrijker vinden dan
            de goedkoopste prijs.
          </p>
        </div>
      </section>

      {/* STORY */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#FF6B00] mb-3">Ons verhaal</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A1128] leading-tight">
              Elektrawerk waar we trots op zijn.
            </h2>
            <div className="mt-6 space-y-5 text-slate-600 text-lg leading-relaxed">
              <p>
                Care for Elektra is opgericht met één simpel idee: elektrawerk moet veilig, netjes en
                betrouwbaar zijn. Geen half werk, geen losse eindjes.
              </p>
              <p>
                Als erkend installateur werken we volgens de nieuwste normen. Van een enkele stopcontact tot
                het volledig vernieuwen van een groepenkast met 3-fase aansluiting en laadpaal. We nemen
                de tijd om het écht goed te doen.
              </p>
              <p>
                Wat ons onderscheidt? De liefde voor het vak. Strak weggewerkte kabels, doordachte
                installaties en heldere uitleg. Achteraf mag je onze klus met een lachje aan iedereen laten
                zien.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="about-cta-contact"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF6B00] text-white font-bold hover:bg-[#E66000] transition-colors"
              >
                Neem contact op <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/diensten"
                data-testid="about-cta-services"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-[#0A1128] text-[#0A1128] font-bold hover:bg-[#0A1128] hover:text-white transition-colors"
              >
                Onze diensten
              </Link>
            </div>
          </div>
          <div className="relative">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-slate-100 shadow-xl">
              <img
                src={IMAGES.serviceAllround}
                alt="Vakkundig elektrawerk"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white border border-slate-200 shadow-2xl rounded-2xl p-6 max-w-[260px]">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF6B00] mb-2">
                KvK
              </div>
              <div className="font-display font-black text-2xl text-[#0A1128]">{SITE.kvk}</div>
              <div className="mt-1 text-xs text-slate-500">BTW: {SITE.btw}</div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {SITE.certifications.map((c) => (
                  <span key={c} className="inline-block px-2 py-0.5 rounded-full bg-orange-50 text-[10px] font-bold text-[#FF6B00] uppercase tracking-wider">
                    {c}
                  </span>
                ))}
              </div>
              <div className="mt-3 text-sm text-slate-500">Care for Elektra</div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-[#F8FAFC] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
          <div className="max-w-2xl mb-14">
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#FF6B00] mb-3">Waarom Care for Elektra</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A1128]">
              Zes redenen om ons te bellen.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {VALUES.map((v) => {
              const Icon = v.icon;
              return (
                <div
                  key={v.title}
                  data-testid={`value-${v.title.toLowerCase().replace(/\s|&/g, "-")}`}
                  className="group bg-white border border-slate-200 rounded-2xl p-8 hover:-translate-y-1 hover:shadow-xl transition-transform"
                >
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-md bg-[#0A1128] text-[#FF6B00] group-hover:bg-[#FF6B00] group-hover:text-white transition-colors mb-5">
                    <Icon className="w-5 h-5" />
                  </span>
                  <h3 className="font-display text-xl font-bold text-[#0A1128] mb-2">{v.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{v.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
